import {
  call,
  put,
  take,
  race,
  takeEvery,
  takeLatest,
  delay,
  select,
  debounce
} from "redux-saga/effects";
import {
  eventError,
  loadingEvents,
  types,
  fetchCriticalUnresolvedEventsSuccess,
  fetchCriticalUnresolvedEventsFailure,
  appendEventSuccess,
  fetchIncidentsByFloorSuccess,
  fetchIncidentsByFloorFailure,
  fetchIncidentsByFloorRequest,
  setSiteStatsCache,
  setSiteStatsPending,
  checkResolveStatusSuccess,
  checkResolveStatusFailure,
  startResolveStatusPolling,
  stopResolveStatusPolling,
} from "../actions";
import { fetchedEvents } from "../actions";
import {
  getCameraEventMap,
  getCriticalUnresolvedEvents,
} from "../../model/eventService";
import { getIncidentsData } from "../../global/utility/Utils";
import { fetchIncidentsByFloorStats, checkResolveBulkStatus } from "../../api/IncidentsManager";
import { RESOLVE_STATUS } from "../../global/constants/constants";

const activeSession = (state) => state.sessionReducer.activeSession;
const getSelectedSiteId = (state) => state.sitesReducer.selectedSiteId;
const POLLING_DELAY = 30000; // Poll every 30 seconds
const DEBOUNCE_DELAY = 1000;
const DEBOUNCE_DELAY_INCIDENTS = 200;
const RESOLVE_STATUS_POLLING_DELAY = 15000; // Poll status every 15 seconds
const getCameraList = (state) => state.deviceReducer.cameraList;
function* EventSaga() {
  yield takeLatest(types.START_POLLING_FOR_EVENTS, startPollingEvents);
  yield takeEvery(types.STOP_POLLING_FOR_EVENTS, stopPollingEvents);
  yield debounce(DEBOUNCE_DELAY, types.GET_EVENTS, pollEventService);
  yield takeEvery(types.APPEND_EVENT, appendEventsSaga)
  yield debounce(
    DEBOUNCE_DELAY,
    types.FETCH_CRITICAL_UNRESOLVED_EVENTS_REQUEST,
    fetchCriticalUnresolvedEventsSaga
  );
  yield debounce(DEBOUNCE_DELAY_INCIDENTS,types.FETCH_INCIDENTS_BY_FLOOR_REQUEST, fetchIncidentsByFloorSaga);
  yield takeEvery(types.CHECK_RESOLVE_STATUS_REQUEST, checkResolveStatusSaga);
  yield takeLatest(types.START_RESOLVE_STATUS_POLLING, startResolveStatusPollingSaga);

}

function* pollEventService(action) {
  try {
    yield put(loadingEvents(true));
    yield put(eventError(false));
    const session = yield select(activeSession);
    const cameraList = yield select(getCameraList);
    const eventData = yield call(getCriticalUnresolvedEvents, session.activeSession, false, action?.payload);
    const ceMap = getCameraEventMap(eventData.res, cameraList);
    yield put(fetchedEvents(eventData.res, ceMap));
    yield put(loadingEvents(false));
  } catch (error) {
    console.log("Error while getting Events -> " + error);
    yield put(eventError(true));
    yield put(loadingEvents(false));
  }
}

let shouldPollForEvents = true;
function* startPollingEvents() {
  shouldPollForEvents = true;
  yield call(pollEventService);
  while (shouldPollForEvents) {
    yield delay(POLLING_DELAY);
    yield call(pollEventService);
  }
}

function* stopPollingEvents() {
  shouldPollForEvents = false;
}

// Multiple independent triggers (websocket push, evidence-status poll,
// account/site-list change, manual refresh) all dispatch
// FETCH_CRITICAL_UNRESOLVED_EVENTS_REQUEST, which is only debounced — not
// takeLatest — so under load two worker instances can be in-flight at once.
// This counter tags each worker invocation so a stale (superseded) response
// can be detected and discarded instead of clobbering a newer request's
// result (NPDIOTS-9549).
let latestCriticalEventsRequestId = 0;

export function* fetchCriticalUnresolvedEventsSaga(action) {
  const requestId = ++latestCriticalEventsRequestId;
  try {
    const shouldUpdateSilently = action?.silentUpdate || false;
    if (!shouldUpdateSilently) {
      yield put(loadingEvents(true));
    }
    yield put(eventError(false));

    const session = yield select(activeSession);
    const sites = action?.payload?.sites || action?.payload;
    const result = yield call(getCriticalUnresolvedEvents, session.activeSession, true, sites);

    if (requestId !== latestCriticalEventsRequestId) {
      // A newer request has started since this one began — its own outcome
      // will (or already did) win, so this stale response is discarded.
      return;
    }

    if(!result) {
      throw new Error("Something went wrong when fetching events");
    }

    const events = result?.res;
    const count = parseInt(result?.headers?.XTotalCount);

    yield put(fetchCriticalUnresolvedEventsSuccess(events, count));
    yield put(loadingEvents(false));

    const siteId = yield select(getSelectedSiteId);
    if (siteId) {
      yield put(fetchIncidentsByFloorRequest(siteId));
    }
  } catch (error) {
    if (requestId !== latestCriticalEventsRequestId) {
      return;
    }
    yield put(loadingEvents(false));
    yield put(eventError(true));
    yield put({ type: types.FETCH_CRITICAL_UNRESOLVED_EVENTS_FAILURE });
  }
}

function* appendEventsSaga(action) {
  try {
    const cameraList = yield select(getCameraList);
    const ceMap = getCameraEventMap(action.eventData, cameraList);

    // Dispatch action to update store with appended data
    yield put(appendEventSuccess(action.eventData, ceMap));
  } catch (error) {
    console.log("Error while appending events -> " + error);
    yield put(eventError(true));
  }
}

// Every winning fetchCriticalUnresolvedEventsSaga dispatches
// FETCH_INCIDENTS_BY_FLOOR_REQUEST for the same siteId, which is only
// debounced (not takeLatest) — so under load two worker instances can be
// in-flight for the same site at once. incidentsByFloor in the reducer is a
// single unkeyed slice, so a stale response can overwrite fresher data.
// Same requestId-guard pattern as fetchCriticalUnresolvedEventsSaga.
let latestIncidentsByFloorRequestId = 0;

// setSiteStatsPending is per-siteId, but was previously reset in `finally`
// only when this worker was still globally "latest" — even across different
// sites. A worker superseded before completing (e.g. a failed stale request)
// would then never reset ITS OWN site's pending flag, since no other worker
// instance ever touches that exact siteId slot — leaving it stuck `true` for
// the rest of the session (that site becomes permanently unfetchable via the
// FloorContainer hover-fetch gate). Tracked per-site in-flight counts instead:
// only clear pending once every in-flight request for that exact site has
// settled — this also avoids a newer worker for the SAME site having its
// pending flag prematurely cleared by an older, still-settling sibling.
const inFlightCountBySite = {};

export function* fetchIncidentsByFloorSaga(action) {
  const siteId = action.payload;
  const requestId = ++latestIncidentsByFloorRequestId;
  inFlightCountBySite[siteId] = (inFlightCountBySite[siteId] || 0) + 1;
  try {
    const session = yield select(activeSession);

    if (!session?.activeSession?.accountId || !siteId) {
      throw new Error("Missing accountId or siteId");
    }

    yield put(setSiteStatsPending(siteId, true));
    const response = yield call(
      fetchIncidentsByFloorStats,
      session.activeSession.accountId,
      siteId
    );

    // Cache is keyed by siteId and stays correct regardless of staleness — a
    // superseded worker still returned a genuine result for its own site, so
    // write it unconditionally to avoid a redundant refetch on the next hover
    // back to that site.
    yield put(setSiteStatsCache(siteId, response));

    if (requestId !== latestIncidentsByFloorRequestId) {
      // A newer request (possibly for a different site) has since started —
      // only the unkeyed "currently displayed floor" slice below must skip it.
      return;
    }

    yield put(fetchIncidentsByFloorSuccess(response));
  } catch (error) {
    console.log("Error while fetching incidents by floor -> " + error);
    if (requestId !== latestIncidentsByFloorRequestId) {
      return;
    }
    yield put(fetchIncidentsByFloorFailure(error.message));
  } finally {
    inFlightCountBySite[siteId] -= 1;
    if (inFlightCountBySite[siteId] <= 0) {
      yield put(setSiteStatsPending(siteId, false));
    }
  }
}

export function* checkResolveStatusSaga(action) {
  const accountID = action.payload;
  const isPostSubmit = action.isPostSubmit || false;

  try {
    if (!accountID) {
      throw new Error("Missing accountID");
    }

    const statusData = yield call(checkResolveBulkStatus, accountID);

    if (statusData.status === RESOLVE_STATUS.IN_PROGRESS) {
      yield put(checkResolveStatusSuccess(statusData));
      yield put(startResolveStatusPolling(accountID));
    } else if (statusData.status === RESOLVE_STATUS.IDLE && isPostSubmit) {
      // Async resolve completed before first status check — treat as COMPLETE
      yield put(checkResolveStatusSuccess({ status: RESOLVE_STATUS.COMPLETE }));
    } else {
      // Page load check — IDLE means nothing in progress
      yield put(checkResolveStatusSuccess(statusData));
    }
  } catch (error) {
    console.log("Error while checking resolve status -> " + error);
    if (isPostSubmit) {
      // Bulk resolve was submitted — start polling even if initial check failed
      yield put(startResolveStatusPolling(accountID));
    } else {
      yield put(checkResolveStatusFailure(error.message || "Failed to check resolution status"));
    }
  }
}

export function* resolveStatusPollLoop(accountID) {
  while (true) {
    yield delay(RESOLVE_STATUS_POLLING_DELAY);
    try {
      const statusData = yield call(checkResolveBulkStatus, accountID);

      // Polling only runs after IN_PROGRESS was detected, so inProgress:false here = just completed
      if (statusData.status === RESOLVE_STATUS.IDLE) {
        yield put(checkResolveStatusSuccess({ status: RESOLVE_STATUS.COMPLETE }));
        yield put(stopResolveStatusPolling());
        break;
      } else {
        yield put(checkResolveStatusSuccess(statusData));
      }
    } catch (error) {
      // Transient error — log and continue polling on next cycle
      console.log("Error while polling resolve status -> " + error);
    }
  }
}

function* startResolveStatusPollingSaga(action) {
  const accountID = action.payload;

  // Outer race cancels the poll loop at ANY point (delay OR mid-API-call)
  // when STOP_RESOLVE_STATUS_POLLING fires (e.g. component unmount)
  yield race({
    poll: call(resolveStatusPollLoop, accountID),
    stop: take(types.STOP_RESOLVE_STATUS_POLLING),
  });
}

export default EventSaga;
